import {
    collection,
    doc,
    updateDoc,
    query,
    where,
    orderBy,
    onSnapshot,
    addDoc,
    serverTimestamp,
    getDoc,
    getDocs,
    arrayUnion,
    writeBatch
  } from 'firebase/firestore';
  import { db } from '../config/firebase';
  import { ChatRoom, ChatMessage } from '../types/chat.types';
  
  class ChatService {
    async initializeSystemChats(): Promise<void> {
      const batch = writeBatch(db);
      
      // Check and create global provider chat
      const providerChatId = 'global-provider-chat';
      const providerChatRef = doc(db, 'chatRooms', providerChatId);
      const providerChatDoc = await getDoc(providerChatRef);
      
      if (!providerChatDoc.exists()) {
        batch.set(providerChatRef, {
          id: providerChatId,
          name: 'Provider Team Chat',
          chatType: 'global-provider',
          allowedRoles: ['provider_admin', 'provider_user'],
          participants: [],
          participantNames: {},
          createdBy: 'system',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          isArchived: false
        });
      }
      
      // Check and create global staff chat
      const staffChatId = 'global-staff-chat';
      const staffChatRef = doc(db, 'chatRooms', staffChatId);
      const staffChatDoc = await getDoc(staffChatRef);
      
      if (!staffChatDoc.exists()) {
        batch.set(staffChatRef, {
          id: staffChatId,
          name: 'All Staff Chat',
          chatType: 'global-staff',
          allowedRoles: ['provider_admin', 'provider_user', 'field'],
          participants: [],
          participantNames: {},
          createdBy: 'system',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          isArchived: false
        });
      }
      
      await batch.commit();
    }
  
    async initializeProjectChats(projectId: string, projectName: string): Promise<void> {
      const batch = writeBatch(db);
      
      // Create project admin chat
      const adminChatId = `project-${projectId}-admin`;
      const adminChatRef = doc(db, 'chatRooms', adminChatId);
      const adminChatDoc = await getDoc(adminChatRef);
      
      if (!adminChatDoc.exists()) {
        batch.set(adminChatRef, {
          id: adminChatId,
          name: `${projectName} - Admin Chat`,
          projectId: projectId,
          chatType: 'project-admin',
          allowedRoles: ['provider_admin', 'provider_user', 'client_admin'],
          participants: [],
          participantNames: {},
          createdBy: 'system',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          isArchived: false
        });
      }
      
      // Create project all participants chat
      const allChatId = `project-${projectId}-all`;
      const allChatRef = doc(db, 'chatRooms', allChatId);
      const allChatDoc = await getDoc(allChatRef);
      
      if (!allChatDoc.exists()) {
        batch.set(allChatRef, {
          id: allChatId,
          name: `${projectName} - Team Chat`,
          projectId: projectId,
          chatType: 'project-all',
          participants: [],
          participantNames: {},
          createdBy: 'system',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          isArchived: false
        });
      }
      
      await batch.commit();
    }
  
    async getUserChatRooms(userId: string, userRole: string, userProjects: string[]): Promise<ChatRoom[]> {
      const rooms: ChatRoom[] = [];
      
      // Get global chats based on role
      if (['provider_admin', 'provider_user'].includes(userRole)) {
        const providerChat = await getDoc(doc(db, 'chatRooms', 'global-provider-chat'));
        if (providerChat.exists()) {
          rooms.push({ id: providerChat.id, ...providerChat.data() } as ChatRoom);
        }
      }
      
      if (['provider_admin', 'provider_user', 'field'].includes(userRole)) {
        const staffChat = await getDoc(doc(db, 'chatRooms', 'global-staff-chat'));
        if (staffChat.exists()) {
          rooms.push({ id: staffChat.id, ...staffChat.data() } as ChatRoom);
        }
      }
      
      // Get project chats
      for (const projectId of userProjects) {
        // Admin chat
        if (['provider_admin', 'provider_user', 'client_admin'].includes(userRole)) {
          const adminChat = await getDoc(doc(db, 'chatRooms', `project-${projectId}-admin`));
          if (adminChat.exists()) {
            rooms.push({ id: adminChat.id, ...adminChat.data() } as ChatRoom);
          }
        }
        
        // All participants chat
        const allChat = await getDoc(doc(db, 'chatRooms', `project-${projectId}-all`));
        if (allChat.exists()) {
          rooms.push({ id: allChat.id, ...allChat.data() } as ChatRoom);
        }
      }
      
      // Get custom rooms where user is a participant
      const customQuery = query(
        collection(db, 'chatRooms'),
        where('chatType', '==', 'custom'),
        where('participants', 'array-contains', userId),
        where('isArchived', '==', false)
      );
      
      const customSnapshot = await getDocs(customQuery);
      customSnapshot.forEach((doc) => {
        rooms.push({ id: doc.id, ...doc.data() } as ChatRoom);
      });
      
      return rooms;
    }
  
    subscribeToUserRooms(userId: string, userRole: string, userProjects: string[], callback: (rooms: ChatRoom[]) => void) {
      // This is a simplified version - in production, you'd want to set up proper listeners
      const fetchRooms = async () => {
        const rooms = await this.getUserChatRooms(userId, userRole, userProjects);
        callback(rooms);
      };
      
      fetchRooms();
      
      // Set up interval to refresh (in production, use proper Firestore listeners)
      const interval = setInterval(fetchRooms, 5000);
      
      return () => clearInterval(interval);
    }
  
    subscribeToArchivedRooms(projectId: string, callback: (rooms: ChatRoom[]) => void) {
      const q = query(
        collection(db, 'chatRooms'),
        where('projectId', '==', projectId),
        where('isArchived', '==', true),
        orderBy('archivedAt', 'desc')
      );
  
      return onSnapshot(q, (snapshot) => {
        const rooms: ChatRoom[] = [];
        snapshot.forEach((doc) => {
          rooms.push({ id: doc.id, ...doc.data() } as ChatRoom);
        });
        callback(rooms);
      });
    }
  
    async archiveRoom(roomId: string, userId: string): Promise<void> {
      try {
        await updateDoc(doc(db, 'chatRooms', roomId), {
          isArchived: true,
          archivedAt: serverTimestamp(),
          archivedBy: userId,
          updatedAt: serverTimestamp()
        });
      } catch (error) {
        console.error('Error archiving room:', error);
        throw error;
      }
    }
  
    async unarchiveRoom(roomId: string): Promise<void> {
      try {
        await updateDoc(doc(db, 'chatRooms', roomId), {
          isArchived: false,
          archivedAt: null,
          archivedBy: null,
          updatedAt: serverTimestamp()
        });
      } catch (error) {
        console.error('Error unarchiving room:', error);
        throw error;
      }
    }
  
    subscribeToMessages(roomId: string, callback: (messages: ChatMessage[]) => void) {
      const q = query(
        collection(db, 'chatMessages'),
        where('roomId', '==', roomId),
        orderBy('timestamp', 'asc')
      );
  
      return onSnapshot(q, (snapshot) => {
        const messages: ChatMessage[] = [];
        snapshot.forEach((doc) => {
          messages.push({ id: doc.id, ...doc.data() } as ChatMessage);
        });
        callback(messages);
      });
    }
  
    async sendMessage(message: Omit<ChatMessage, 'id' | 'timestamp' | 'readBy'>): Promise<void> {
      try {
        // Add the message
        const messageData = {
          ...message,
          timestamp: serverTimestamp(),
          readBy: [message.senderId],
          edited: false
        };
        
        await addDoc(collection(db, 'chatMessages'), messageData);
  
        // Update the room's last message info
        await updateDoc(doc(db, 'chatRooms', message.roomId), {
          lastMessage: message.content,
          lastMessageAt: serverTimestamp(),
          lastMessageSenderId: message.senderId,
          lastMessageSenderName: message.senderName,
          updatedAt: serverTimestamp()
        });
      } catch (error) {
        console.error('Error sending message:', error);
        throw error;
      }
    }
  
    async markMessageAsRead(messageId: string, userId: string): Promise<void> {
      try {
        await updateDoc(doc(db, 'chatMessages', messageId), {
          readBy: arrayUnion(userId)
        });
      } catch (error) {
        console.error('Error marking message as read:', error);
        throw error;
      }
    }
  
    async getRoom(roomId: string): Promise<ChatRoom | null> {
      try {
        const docSnap = await getDoc(doc(db, 'chatRooms', roomId));
        if (docSnap.exists()) {
          return { id: docSnap.id, ...docSnap.data() } as ChatRoom;
        }
        return null;
      } catch (error) {
        console.error('Error getting room:', error);
        throw error;
      }
    }
  
    async addParticipant(roomId: string, userId: string, userName: string): Promise<void> {
      try {
        const roomRef = doc(db, 'chatRooms', roomId);
        const roomDoc = await getDoc(roomRef);
        
        if (roomDoc.exists()) {
          const currentData = roomDoc.data();
          const updatedParticipantNames = {
            ...currentData.participantNames,
            [userId]: userName
          };
          
          await updateDoc(roomRef, {
            participants: arrayUnion(userId),
            participantNames: updatedParticipantNames,
            updatedAt: serverTimestamp()
          });
        }
      } catch (error) {
        console.error('Error adding participant:', error);
        throw error;
      }
    }
  
    async createRoom(room: Omit<ChatRoom, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
      try {
        const roomData = {
          ...room,
          chatType: room.chatType || 'custom',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          isArchived: false
        };
        
        const docRef = await addDoc(collection(db, 'chatRooms'), roomData);
        return docRef.id;
      } catch (error) {
        console.error('Error creating chat room:', error);
        throw error;
      }
    }
  }
  
  export const chatService = new ChatService();